import React, { useEffect, useState } from 'react';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '../shadcn-ui/drawer';
import { Button } from '../shadcn-ui/button';
import { Loader2 } from 'lucide-react';
import { RecordConsumptionSchema } from '@/schema/storage-lot-schema';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from '../FormField';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import { createStorageConsumption } from '@/network/api.service';

const RecordConsumptionDrawer = ({ stage, fetchStorageDetailLots, storageDetailLots, fetchStorageConsumptions }) => {
  const { lot_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    watch,
    reset,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(RecordConsumptionSchema),
    defaultValues: {
      lot_id: lot_id,
      date_of_consumption: new Date(),
      storage_label: '',
      storage_lot_id: '',
      weight: null,
      reason: null,
    },
  });

  const selectedStorageLotId = watch('storage_lot_id');

  useEffect(() => {
    if (selectedStorageLotId && storageDetailLots) {
      const selectedLot = storageDetailLots.find(lot => lot._id === selectedStorageLotId);
      if (selectedLot && selectedLot.storage_label) {
        // Extract first two parts (e.g., "437_8" from "437_8_600_4.8_4.8")
        const labelParts = selectedLot.storage_label.split('_');
        const truncatedLabel = labelParts.slice(0, 2).join('_');
        setValue('storage_label', truncatedLabel);
        setSelectedLot(selectedLot);
      }
    }
  }, [selectedStorageLotId, storageDetailLots, setValue]);

  const handleOpenChange = open => {
    if (!open) {
      reset();
    }
    setOpen(open);
  };

  const onSubmit = async data => {
    if (getValues('weight') > (selectedLot?.total_weight || 0) - (selectedLot?.consumption_weight || 0)) {
      toast.error('Weight cannot be greater than the available weight');
      return;
    }
    try {
      setLoading(true);
      const { data: respData } = await createStorageConsumption(data);

      if (respData?.success) {
        toast.success('Storage consumption created successfully');
        setOpen(false);
        reset();
        fetchStorageDetailLots();
        fetchStorageConsumptions();
      }
    } catch (error) {
      console.log(error);

      toast.error('Failed to record storage consumption');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button onClick={() => setOpen(true)} disabled={stage !== 'Storage'}>
          Record consumption
        </Button>
      </DrawerTrigger>

      <DrawerContent className="max-h-[90vh]">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 pb-5">
          <DrawerHeader className="text-left">
            <DrawerTitle>Record Consumption</DrawerTitle>
          </DrawerHeader>
          <FormField
            date
            label="Date of consumption"
            name="date_of_consumption"
            required
            control={control}
            disabled={loading}
            errors={errors}
          />
          <FormField
            required
            select
            options={storageDetailLots?.map(lot => ({ label: lot.storage_label, value: lot._id }))}
            label="Lot"
            name="storage_lot_id"
            disabled={loading}
            placeholder="Select storage lot"
            className="bg-transparent"
            errors={errors}
            control={control}
          />
          <FormField
            required
            type="number"
            label="Weight"
            name="weight"
            step="0.01"
            placeholder="e.g., 4.2"
            className="bg-transparent"
            register={register}
            disabled={loading}
            errors={errors}
          />

          <FormField
            required
            textarea
            label="Reason"
            name="reason"
            placeholder="Enter reason for consumption here..."
            className="bg-transparent"
            register={register}
            disabled={loading}
            errors={errors}
          />

          <DrawerFooter className="flex flex-row justify-end gap-2 border-t">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spinSlow" />}
              {loading ? 'Adding...' : 'Add lot'}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};

export default RecordConsumptionDrawer;
